import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { AiBlueprintSkeletonComponent } from '../shared/components/ai-blueprint-skeleton/ai-blueprint-skeleton.component';
import { ApiService } from '../_services/api.service';
import { AuthService } from '../_services/auth.service';
import { Template } from '../_interfaces/template';
import { AiBlueprintRow } from '../_interfaces/ai-blueprint';
import { DefaultTemplate } from '../_interfaces/user-settings.interface';
import { LoadingVisibilityController } from '../shared/utils/loading-visibility';
import { AnalyticsService } from '../_services/analytics.service';

const PAGE_SIZE = 15;
const BLUEPRINT_COUNT_LIMIT = 100;

@Component({
  selector: 'app-ai-blueprints',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    AiBlueprintSkeletonComponent,
    TranslateModule
  ],
  templateUrl: './ai-blueprints.component.html',
  styleUrl: './ai-blueprints.component.scss',
  host: {
    class: 'block h-full p-4 md:px-10 md:pt-8 md:pb-14'
  }
})
export class AiBlueprintsComponent implements OnInit, OnDestroy {
  private readonly analytics = inject(AnalyticsService);

  displayedColumns: string[] = ['name', 'created', 'updated', 'default'];
  dataSource = new MatTableDataSource<AiBlueprintRow>([]);

  isLoading = false;
  isLoadingMore = false;
  showLoadingSkeleton = false;
  showLoadingMoreSkeleton = false;
  hasError = false;
  hasMore = true;
  private currentPage = 0;

  @ViewChild('tableWrapper') tableWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('mobileListWrapper') mobileListWrapper?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private readonly loadingVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingSkeleton = visible;
  });
  private readonly loadingMoreVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingMoreSkeleton = visible;
  });

  private defaultTemplateKeys = new Set<string>();

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private router: Router
  ) {}

  openBlueprint(row: AiBlueprintRow): void {
    this.analytics.track('blueprint_opened', { blueprint_id: String(row.id) });
    this.router.navigate(['/ai-blueprints', row.id]);
  }

  async onToggleDefault(row: AiBlueprintRow, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    const newValue = !row.isDefault;
    row.isDefault = newValue;

    const current = this.authService.getUser()?.settings?.copilotTemplates ?? [];
    let updated: DefaultTemplate[];

    if (newValue) {
      updated = [...current, { id: row.id, type: 'user' as const }];
    } else {
      updated = current.filter(t => !(t.id === row.id && t.type === 'user'));
    }

    try {
      await this.authService.updateUser({ settings: { copilotTemplates: updated } });
      this.snackBar.open(
        this.translate.instant(newValue ? 'AI blueprint set as default' : 'AI blueprint removed as default'),
        this.translate.instant('Close'),
        { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    } catch {
      row.isDefault = !newValue;
      this.snackBar.open(
        this.translate.instant('Unable to update default, please try again'),
        this.translate.instant('Close'),
        { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    }
  }

  ngOnInit(): void {
    this.loadInitial();
  }

  ngOnDestroy(): void {
    this.loadingVisibility.destroy();
    this.loadingMoreVisibility.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshDefaultKeys(): void {
    const defaults = this.authService.getUser()?.settings?.copilotTemplates ?? [];
    this.defaultTemplateKeys = new Set(defaults.map(t => `${t.id}-${t.type}`));
  }

  private async loadInitial(): Promise<void> {
    this.isLoading = true;
    this.loadingVisibility.start();
    this.hasError = false;
    this.hasMore = true;
    this.currentPage = 0;
    this.dataSource.data = [];
    this.refreshDefaultKeys();

    try {
      const templates = await this.apiService.getCopilotTemplates({
        searchLimit: PAGE_SIZE,
        searchPage: this.currentPage
      });
      this.dataSource.data = this.mapTemplatesToBlueprints(templates);
      this.hasMore = templates.length === PAGE_SIZE;
    } catch {
      this.hasError = true;
      this.snackBar.open(
        this.translate.instant('Unable to load AI blueprints.'),
        this.translate.instant('Close'),
        { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    } finally {
      this.isLoading = false;
      this.loadingVisibility.stop();
    }

    if (!this.hasError) {
      void this.trackBlueprintsPageViewed();
    }

    if (!this.hasError && this.hasMore && this.dataSource.data.length === 0) {
      this.loadMore();
    }
  }

  private async trackBlueprintsPageViewed(): Promise<void> {
    try {
      const allTemplates = await this.apiService.getCopilotTemplates();
      const userBlueprintCount = allTemplates.filter(t => t.type === 'user').length;
      this.analytics.track('blueprints_page_viewed', {
        blueprint_count: userBlueprintCount,
        has_default_set: this.defaultTemplateKeys.size > 0,
      });
    } catch {
      // Page view analytics must not block the UI.
    }
  }

  private async loadMore(): Promise<void> {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.loadingMoreVisibility.start();

    let addedRows = 0;
    try {
      const nextPage = this.currentPage + 1;
      const templates = await this.apiService.getCopilotTemplates({
        searchLimit: PAGE_SIZE,
        searchPage: nextPage
      });
      const newRows = this.mapTemplatesToBlueprints(templates);
      addedRows = newRows.length;
      this.dataSource.data = [...this.dataSource.data, ...newRows].sort((a, b) => {
        const aTime = a.updatedAt?.getTime() ?? 0;
        const bTime = b.updatedAt?.getTime() ?? 0;
        return bTime - aTime;
      });
      this.currentPage = nextPage;
      this.hasMore = templates.length === PAGE_SIZE;
    } catch {
      this.snackBar.open(
        this.translate.instant('Unable to load more AI blueprints.'),
        this.translate.instant('Close'),
        { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    } finally {
      this.isLoadingMore = false;
      this.loadingMoreVisibility.stop();
    }

    if (this.hasMore && addedRows === 0) {
      this.loadMore();
    }
  }

  onTableScroll(): void {
    this.loadMoreWhenNearBottom(this.tableWrapper?.nativeElement);
  }

  onMobileListScroll(): void {
    this.loadMoreWhenNearBottom(this.mobileListWrapper?.nativeElement);
  }

  private loadMoreWhenNearBottom(el?: HTMLElement): void {
    if (!el) return;
    const threshold = 200;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMore();
    }
  }

  private mapTemplatesToBlueprints(templates: Template[]): AiBlueprintRow[] {
    return templates
      .filter(t => t.type === 'user')
      .map(t => {
        const tWithDates = t as Template & { created_at?: string; updated_at?: string };
        const createdAt = tWithDates.created_at ? toDate(tWithDates.created_at) : null;
        const updatedAt = tWithDates.updated_at ? toDate(tWithDates.updated_at) : null;
        return {
          id: t.id,
          name: t.title,
          createdAt,
          updatedAt,
          isDefault: this.defaultTemplateKeys.has(`${t.id}-${t.type}`)
        };
      })
      .sort((a, b) => {
        const aTime = a.updatedAt?.getTime() ?? 0;
        const bTime = b.updatedAt?.getTime() ?? 0;
        return bTime - aTime;
      });
  }
}

function toDate(value: string): Date | null {
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}
